/**
 * VistoeraTASK-00581 — Canvas autosave vs account switch.
 *
 * The canvas already debounces autosave (`DebouncedSaveController`). Account
 * switch used to treat `pending`/`saving` as "unsaved changes" and always show
 * a confirm dialog — even though the same debounce is fire-and-forget when the
 * user leaves for the workbench. Product rule: auto-flush first; only surface a
 * dialog when persistence actually fails.
 */

import type { SaveStatus } from '../workspace/contentProduction/canvas/debouncedSaveController'

export interface CanvasAccountSwitchGuard {
  /** Live autosave status from the mounted CanvasBoard (if any). */
  getStatus: () => SaveStatus
  /** Flush pending payload and wait for the real PUT outcome. */
  saveBeforeSwitch: () => Promise<boolean>
  /** Drop a pending timer/payload without writing (explicit discard only). */
  discardPending: () => void
}

let activeGuard: CanvasAccountSwitchGuard | null = null

export function registerCanvasAccountSwitchGuard(guard: CanvasAccountSwitchGuard | null): void {
  activeGuard = guard
}

/** True when a CanvasBoard is mounted and has a pending or in-flight autosave. */
export function canvasHasUnsavedChanges(): boolean {
  if (!activeGuard) return false
  const status = activeGuard.getStatus()
  return status === 'pending' || status === 'saving' || status === 'error'
}

export type CanvasSwitchPrepResult = 'ready' | 'save_failed'

/**
 * Prepare the active canvas (if any) for an identity switch, under the CURRENT
 * session. Must run BEFORE `switch-account` so A's PUT never rides B's cookie.
 *
 * - No canvas / idle / already saved → `ready` (no dialog).
 * - pending / saving / error → flush once; `ready` on success, `save_failed` on failure.
 */
export async function prepareCanvasForAccountSwitch(): Promise<CanvasSwitchPrepResult> {
  if (!activeGuard) return 'ready'
  const status = activeGuard.getStatus()
  if (status === 'idle' || status === 'saved') return 'ready'
  const ok = await activeGuard.saveBeforeSwitch()
  return ok ? 'ready' : 'save_failed'
}

export function discardCanvasPendingForAccountSwitch(): void {
  activeGuard?.discardPending()
}

/** Explicit modal actions after a failed auto-flush. */
export async function executeCanvasGuardedSwitch(
  choice: 'save' | 'discard',
  proceed: () => Promise<void>,
): Promise<void> {
  if (!activeGuard) {
    await proceed()
    return
  }
  if (choice === 'save') {
    const ok = await activeGuard.saveBeforeSwitch()
    if (!ok) return
  } else {
    activeGuard.discardPending()
  }
  await proceed()
}
